const AbstractCloverTransaction = require('@/lib/PaymentMethods/Clover/AbstractCloverTransaction');
const CloverApiVersion = require('@/lib/PaymentMethods/Clover/Enums/CloverApiVersion');
const cloverAxiosInstanceFactory = require('@/lib/PaymentMethods/Clover/cloverAxiosInstanceFactory');
const CloverDeviceCancelInFlightRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverDeviceCancelInFlightRequestRecord');
const CloverDeviceListRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverDeviceListRequestRecord');
const CloverDeviceReadTipRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverDeviceReadTipRequestRecord');
const CloverDeviceStatusRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverDeviceStatusRequestRecord');
const CloverDeviceThankYouRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverDeviceThankYouRequestRecord');
const CloverDeviceVerifyConnectivityRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverDeviceVerifyConnectivityRequestRecord');
const CloverDeviceWelcomeRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverDeviceWelcomeRequestRecord');
const CloverPaymentAndRefundResult = require('@/lib/PaymentMethods/Clover/Enums/CloverPaymentAndRefundResult');
const CloverPaymentAndRefundVoidReason = require('@/lib/PaymentMethods/Clover/Enums/CloverPaymentAndRefundVoidReason');
const CloverReceiptCreditRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverReceiptCreditRequestRecord');
const CloverReceiptDeliveryMethod = require('@/lib/PaymentMethods/Clover/Enums/CloverReceiptDeliveryMethod');
const CloverReceiptPaymentRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverReceiptPaymentRequestRecord');
const CloverReceiptRefundRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverReceiptRefundRequestRecord');
const CloverReceiptVoidRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverReceiptVoidRequestRecord');
const CloverRefundTransaction = require('@/lib/PaymentMethods/Clover/CloverRefundTransaction');
const CloverTransaction = require('@/lib/PaymentMethods/Clover/CloverTransaction');
const CloverTransactionCreditRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverTransactionCreditRequestRecord');
const CloverTransactionGetPaymentByExternalPaymentIdRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverTransactionGetPaymentByExternalPaymentIdRequestRecord');
const CloverTransactionGetPaymentByPaymentIdRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverTransactionGetPaymentByPaymentIdRequestRecord');
const CloverTransactionPaymentRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverTransactionPaymentRequestRecord');
const CloverTransactionState = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionState');
const CloverTransactionType = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionType');
const CloverTransactionVoidRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/CloverTransactionVoidRequestRecord');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const { ucwords } = require('@/functions/strings');

/**
 * @constructor
 *
 * @param $timeout
 * @param {CloverConfig} cloverConfig
 * @param {CurrentDevice} currentDevice
 * @param {CurrencyService} currencyService
 * @param {ReceiptPrinter} receiptPrinter
 * @param {ToastFactory} toastFactory
 */
const CloverDriver = function (
    $timeout,
    cloverConfig,
    currentDevice,
    currencyService,
    receiptPrinter,
    toastFactory
) {
    this.$timeout = $timeout;

    /**
     * @type {ConsoleLogger}
     */
    this.logger = logFactory('CloverDriver');

    /**
     * @type {CurrentDevice}
     */
    this.currentDevice = currentDevice;

    /**
     * @type {CurrencyService}
     */
    this.currencyService = currencyService;

    /**
     * @type {ReceiptPrinter}
     */
    this.receiptPrinter = receiptPrinter;

    /**
     * @type {ToastFactory}
     */
    this.toastFactory = toastFactory;

    /**
     * @type {string}
     */
    this.paymentMethodRef = PaymentMethodRef.CARD_CLOVER_CHIP_AND_PIN;

    /**
     * @type {CloverConfig}
     */
    this.config = cloverConfig;

    /**
     * @type {CloverAxiosInstance}
     */
    this.cloverAxiosInstance = null;

    // Make it easy to mock rather than using the factory directly.
    this.cloverAxiosInstanceFactory = cloverAxiosInstanceFactory;
};

CloverDriver.prototype = {
    /**
     * Should we validate the terminalId or not?
     *
     * @param {boolean} validateTerminalId
     */
    init(validateTerminalId = true) {
        this.config.validate(validateTerminalId);

        // Almost all our requests will use the Rest Pay Display API, so we'll set this up as the default.
        this.cloverAxiosInstance = cloverAxiosInstanceFactory(this.config, CloverApiVersion.REST_PAY_DISPLAY);
    },

    /**
     * Check if the terminal for this till is online.
     */
    async ping() {
        try {
            const response = await this.cloverAxiosInstance.request(
                new CloverDeviceVerifyConnectivityRequestRecord()
            );
            if (response.data.connected) {
                return true;
            }
            return false;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    },

    /**
     * Check the status of this terminal.
     */
    async status() {
        try {
            const response = await this.cloverAxiosInstance.request(
                new CloverDeviceStatusRequestRecord()
            );
            if (response.data.status) {
                return response.data.status;
            }
            return false;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    },

    /**
     * This cancels the current operation, but we don't know if it was successful because an HTTP 200 response
     * just means that the cancel request was received successfully.
     *
     * @param {?cloverTransaction} currentTransaction
     *
     * @return {Promise<void>}
     */
    async cancelInFlight(currentTransaction) {
        try {
            if (currentTransaction) {
                if (!(currentTransaction instanceof CloverTransaction)) {
                    throw new Error('An instance of CloverTransaction is required');
                }
                currentTransaction.state = CloverTransactionState.ATTEMPTING_TO_CANCEL;
            }
            await this.cloverAxiosInstance.request(new CloverDeviceCancelInFlightRequestRecord());
        } catch (error) {
            this.handleError(error);
        }
    },

    async getPaymentByExternalPaymentId(externalPaymentId) {
        try {
            if (!externalPaymentId || externalPaymentId.trim() === '') {
                throw new Error('externalPaymentId is required');
            }
            return await this.cloverAxiosInstance.request(
                new CloverTransactionGetPaymentByExternalPaymentIdRequestRecord(externalPaymentId)
            );
        } catch (error) {
            this.handleError(error);
            return false;
        }
    },

    async getPaymentByPaymentId(paymentId) {
        try {
            if (!paymentId || paymentId.trim() === '') {
                throw new Error('paymentId is required');
            }
            return await this.cloverAxiosInstance.request(
                new CloverTransactionGetPaymentByPaymentIdRequestRecord(paymentId)
            );
        } catch (error) {
            this.handleError(error);
            return false;
        }
    },

    async getTipAmount(baseAmount) {
        try {
            if (!baseAmount || !Number.isInteger(baseAmount)) {
                throw new Error('baseAmount is required and should be an integer number');
            }

            // @TODO - make the tip suggestions configurable. For now let's have sensible defaults.
            const tipSuggestions = [
                {
                    percentage: 10,
                },
                {
                    percentage: 15,
                },
                // Custom tip is always and option.
                // Skip / No tip is always an option.
                // 4 options looks good on the terminal screen.
            ];

            // If we have a split bill where each payment could add a gratuity then we hit the idempotency check, and
            // we don't need this request to be idempotent anyway.
            this.cloverAxiosInstance.clearIdempotencyKey();

            return await this.cloverAxiosInstance.request(
                new CloverDeviceReadTipRequestRecord(baseAmount, tipSuggestions)
            );
        } catch (error) {
            this.handleError(error);
            return false;
        }
    },

    /**
     * Print a receipt for each in an array of payments.
     *
     * @param {Payment[]} payments
     *
     * @return {Promise<void>}
     */
    async generateReceipts(payments) {
        for (const payment of payments) {
            if (payment.amount > 0) {
                await this.generateReceipt(CloverTransactionType.PAYMENT, payment.transactionRef);
            } else {
                const id = payment.transactionRef.replaceAll('"', '');
                if (payment.originalPaymentID) {
                    await this.generateReceipt(CloverTransactionType.REFUND, id);
                } else {
                    await this.generateReceipt(CloverTransactionType.CREDIT, id);
                }
            }
        }
        await this.displayThankYou();
    },

    /**
     * Print a receipt of the appropriate type.
     * Note: SMS and Email receipts via this mechanism are possible, but we haven't implemented them yet.
     *
     * @param {string} transactionType
     * @param {string} transactionId
     *
     * @return {Promise<Awaited<unknown>[]|boolean>}
     */
    async generateReceipt(transactionType, transactionId) {
        try {
            if (!transactionType || !(CloverTransactionType.hasOwnProperty(transactionType))) {
                throw new Error(transactionType + ' is not a valid transactionType');
            }
            if (!transactionId || transactionId.trim() === '') {
                throw new Error('transactionId is required');
            }

            this.cloverAxiosInstance.clearIdempotencyKey();

            const option = {
                method: CloverReceiptDeliveryMethod.PRINT,
            };

            let receiptRequest = null;

            switch (transactionType) {
                case CloverTransactionType.CREDIT:
                    receiptRequest = new CloverReceiptCreditRequestRecord(transactionId, option);
                    break;
                case CloverTransactionType.PAYMENT:
                    receiptRequest = new CloverReceiptPaymentRequestRecord(transactionId, option);
                    break;
                case CloverTransactionType.REFUND:
                    receiptRequest = new CloverReceiptRefundRequestRecord(transactionId, option);
                    break;
                case CloverTransactionType.VOID:
                    receiptRequest = new CloverReceiptVoidRequestRecord(transactionId, option);
                    break;
            }

            return await this.cloverAxiosInstance.request(receiptRequest);
        } catch (error) {
            // what should we do here?
            this.handleError(error);
            return false;
        }
    },

    async displayThankYou() {
        try {
            return await this.cloverAxiosInstance.request(
                new CloverDeviceThankYouRequestRecord()
            );
        } catch (error) {
            // Ignore any errors.
            return false;
        }
    },

    async displayWelcome() {
        try {
            return await this.cloverAxiosInstance.request(
                new CloverDeviceWelcomeRequestRecord()
            );
        } catch (error) {
            // Ignore any errors.
            return false;
        }
    },

    async getTerminalList() {
        try {
            const cloverAxiosInstance = this.cloverAxiosInstanceFactory(this.config, CloverApiVersion.PLATFORM);
            const response = await cloverAxiosInstance.request(
                new CloverDeviceListRequestRecord(this.config.merchantID)
            );
            if (response.data.elements) {
                return response.data.elements;
            }
            throw new Error('Unexpected response');
        } catch (error) {
            this.handleError(error);
            return null;
        }
    },

    /**
     *
     * @param data
     * @return {string}
     */
    getStatusDetail(data) {
        return JSON.stringify(data);
    },

    /**
     * Check if the terminal for this device is online and available for use.
     */
    async terminalReady() {
        try {
            if (!this.config.xCloverDeviceId) {
                throw new Error('Please select a terminal');
            }
            const response = await this.cloverAxiosInstance.request(
                new CloverDeviceStatusRequestRecord()
            );
            if (response.data.status === 'IDLE') {
                return true;
            }
            // BUSY
            return false;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    },

    createTransaction(amountInMinorUnits, externalPaymentId, externalReferenceId) {
        if (!amountInMinorUnits || !Number.isInteger(amountInMinorUnits)) {
            throw new Error('amountInMinorUnits is required and should be an integer number');
        }
        if (!externalPaymentId || externalPaymentId.trim() === '') {
            throw new Error('externalPaymentId is required');
        }
        if (!externalReferenceId || externalReferenceId.trim() === '') {
            throw new Error('externalReferenceId is required');
        }
        return new CloverTransaction(amountInMinorUnits, externalPaymentId, externalReferenceId);
    },

    /**
     * Create the appropriate request and add it to the transaction object.
     *
     * @param {CloverTransaction} cloverTransaction
     */
    createTransactionRequest(cloverTransaction) {
        if (!cloverTransaction || !(cloverTransaction instanceof CloverTransaction)) {
            throw new Error('An instance of CloverTransaction is required');
        }
        let request = null;
        const externalPaymentId = cloverTransaction.thirdPartyID;
        const externalReferenceId = cloverTransaction.externalReferenceId;
        if (cloverTransaction.amountInMinorUnits >= 0) {
            let amountInMinorUnits = cloverTransaction.amountInMinorUnits;
            if (cloverTransaction.tipAmountInMinorUnits) {
                amountInMinorUnits += cloverTransaction.tipAmountInMinorUnits;
            }
            request = new CloverTransactionPaymentRequestRecord(
                amountInMinorUnits,
                externalPaymentId,
                externalReferenceId,
                cloverTransaction.deviceOptions,
            );
        } else {
            let amountInMinorUnits = -1 * cloverTransaction.amountInMinorUnits;
            request = new CloverTransactionCreditRequestRecord(
                amountInMinorUnits,
                cloverTransaction.deviceOptions,
            );
        }

        cloverTransaction.addTransactionRequest(request);
    },

    /**
     * @param {CloverTransaction|CloverRefundTransaction} currentTransaction
     *
     * @return {Promise<boolean>}
     */
    async sendTransaction(currentTransaction) {
        if (
            !(currentTransaction instanceof CloverTransaction)
            && !(currentTransaction instanceof CloverRefundTransaction)
        ) {
            throw new Error('An instance of CloverTransaction is required');
        }
        try {
            this.cloverAxiosInstance.setIdempotencyKey(currentTransaction.thirdPartyID);
            const response = await this.cloverAxiosInstance.request(currentTransaction.initialRequest);

            if (response.data.payment?.result === CloverPaymentAndRefundResult.SUCCESS) {
                if (currentTransaction.state === CloverTransactionState.ATTEMPTING_TO_CANCEL) {
                    // We tried to cancel, but we weren't quick enough so wer now have to try VOID
                    // We don't add the SUCCESS reponse yet.
                    await this.voidTransaction(
                        currentTransaction,
                        CloverPaymentAndRefundVoidReason.USER_CANCEL,
                        response.data.payment.id,
                    );
                    return;
                }
            }
            currentTransaction.addResponse(response);
        } catch (error) {
            // Capture the error response so we can handle it.
            currentTransaction.addResponse(error.response);
        }
    },

    /**
     * Send a request to void the transaction then poll until we get a result.
     * This could be the current transaction or a previously completed transaction.
     *
     * @param {CloverTransaction|CloverVoidTransaction} currentTransaction
     * @param {string} voidReason
     * @param {string} paymentId
     */
    async voidTransaction(currentTransaction, voidReason, paymentId) {
        try {
            if (!currentTransaction || !(currentTransaction instanceof AbstractCloverTransaction)) {
                throw new Error('An instance of CloverTransaction or CloverVoidTransaction is required');
            }
            if (!voidReason || !CloverPaymentAndRefundVoidReason.hasOwnProperty(voidReason)) {
                throw new Error('voidReason is required');
            }
            if (!paymentId || paymentId.trim() === '') {
                throw new Error('paymentId is required');
            }

            // We need to send an idempotency key, but we can't send the one for the payment.
            // Let's just append to it, so we create a relationship.
            this.cloverAxiosInstance.setIdempotencyKey(
                this.cloverAxiosInstance.getIdempotencyKey() + 'v'
            );

            this.$timeout(() => {
                currentTransaction.state = CloverTransactionState.ATTEMPTING_TO_VOID;
            });
            await this.cloverAxiosInstance.request(
                new CloverTransactionVoidRequestRecord(paymentId, voidReason)
            );
            await this.pollPayment(currentTransaction, paymentId);
        } catch (error) {
            this.handleError(error);
        }
    },

    /**
     * @param {CloverTransaction|CloverVoidTransaction} currentTransaction
     * @param {string} paymentId
     *
     * @return {Promise<*|undefined>}
     */
    async pollPayment(currentTransaction, paymentId) {
        if (!currentTransaction || !(currentTransaction instanceof AbstractCloverTransaction)) {
            throw new Error('An instance of CloverTransaction or CloverVoidTransaction is required');
        }
        if (!paymentId || paymentId.trim() === '') {
            throw new Error('paymentId is required');
        }

        const request = new CloverTransactionGetPaymentByPaymentIdRequestRecord(paymentId);

        const exitStates = [
            // We don't have success because the state is already SUCCESS.
            CloverPaymentAndRefundResult.FAIL,
            CloverPaymentAndRefundResult.VOIDED,
            CloverPaymentAndRefundResult.VOID_FAILED,
        ];

        const progressStates = [
            CloverPaymentAndRefundResult.INITIATED,
            CloverPaymentAndRefundResult.VOIDING,
        ];

        const poll = async (pollRequest) => {
            const response = await this.cloverAxiosInstance.request(pollRequest);

            if (response.status === 200) {
                if (progressStates.includes(response.data.payment.result)) {
                    this.$timeout(() => {
                        currentTransaction.state = ucwords(response.data.payment.result.toLowerCase());
                    });
                }
                if (exitStates.includes(response.data.payment.result)) {
                    this.$timeout(() => {
                        currentTransaction.addResponse(response);
                    });
                    return;
                }
                setTimeout(async () => {
                    await poll(pollRequest);
                }, this.config.pollPeriod);
            }
        };
        // eslint-disable-next-line no-return-await
        return await poll(request);
    },

    /**
     * @param {CloverRefundTransaction} refundTransaction
     *
     * @return {Promise<null>}
     */
    async checkRefundState(refundTransaction) {
        const p = await this.getPaymentByPaymentId(refundTransaction.paymentId);

        if (p.data.payment.refunds && p.data.payment.refunds.length) {
            const mostRecentRefund = p.data.payment.refunds[0];
            if (
                mostRecentRefund.amount === refundTransaction.amountInMinorUnits
                && mostRecentRefund.createdTime > refundTransaction.transactionCreatedAt
            ) {
                // This is the same refund so return it.
                return p.data.payment;
            }
        }

        return null;
    },

    /**
     * 400 The request is invalid, and subsequent calls will continue to fail; do not repeat the call.
     *
     * 401 An invalid bearer token has been provided. The call may be repeated if the provided bearer token is updated.
     *
     * 415 The request is invalid, and contains request data in an unsupported format.
     *     This call may be repeated if the content is changed to a supported format.
     *
     * 500 The request processing failed with an unknown error, and the processing state of the operation is indeterminate.
     *     Do not repeat the call without additional analysis.
     *
     * 501 The device does not have the ability to respond to the requested operation; do not repeat the call.
     *
     * 503 The device is not available because it is currently servicing another call.
     *     This can be repeated at a later time, or cancel may be called to end the current operation.
     *
     * 504 The Device did not respond in a timely manner. This can be repeated at a later time.
     *
     * Note: In addition to errors, the Clover API will usually return a 209 if a request is cancelled.
     *
     * @param error
     */
    handleError(error) {
        let message = error;
        if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
        }
        throw new Error(message);
    },
};

module.exports = CloverDriver;
